// Amazon PPC Audit: reads a Sponsored Products Search Term report in the browser and sorts every search term.
// The file is never uploaded anywhere. The rules engine is exposed as window.BDTPPCAudit so it can be tested.
(() => {
  const BUCKETS = [
    {
      key: 'winner',
      label: 'Winners',
      color: '#1f7a4d',
      action: 'Move to Exact – Winners',
      desc: 'Enough orders at or under your target ACoS. Give them their own exact campaign and negate them where they came from.',
    },
    {
      key: 'promising',
      label: 'Promising',
      color: '#7cc08a',
      action: 'Add to Phrase – Testing',
      desc: 'Converting under target but not proven yet. Test them in a phrase campaign until they reach Winner.',
    },
    {
      key: 'slightly',
      label: 'Slightly Over',
      color: '#f8bd04',
      action: 'Trim the bid',
      desc: 'Selling, but ACoS is above target, up to the Way Over line. A small bid trim usually fixes these.',
    },
    {
      key: 'way',
      label: 'Way Over',
      color: '#e0731f',
      action: 'Cut the bid hard',
      desc: 'Selling, but ACoS is far above target. Cut the bid by up to your max change and watch them.',
    },
    {
      key: 'loser',
      label: 'Losers',
      color: '#c0392b',
      action: 'Negate',
      desc: 'Lots of clicks and no orders. This is your confirmed wasted spend. Negate them.',
    },
    {
      key: 'caution',
      label: 'Caution',
      color: '#8a8173',
      action: 'Watch',
      desc: 'Some clicks and no orders yet. Not enough data to negate. Check again next report.',
    },
    {
      key: 'noise',
      label: 'Noise',
      color: '#d9d3c4',
      action: 'Ignore for now',
      desc: 'Too few clicks to judge. These are hidden from your action plan.',
    },
  ];

  const DEFAULTS = { target: 0.3, wayMult: 1.5, winnerOrders: 3, loserClicks: 30, cautionClicks: 10, maxChange: 0.5 };

  const STOPWORDS = new Set(
    'a an and are as at be by for from in is it of on or the to with your my our you'.split(' ')
  );

  const ASIN = /^b0[a-z0-9]{8}$/i;

  // ---------- File reading ----------

  const decodeXml = (text) =>
    text
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&apos;/g, "'")
      .replace(/&#(\d+);/g, (m, code) => String.fromCodePoint(Number(code)))
      .replace(/&#x([0-9a-f]+);/gi, (m, code) => String.fromCodePoint(parseInt(code, 16)))
      .replace(/&amp;/g, '&');

  // Minimal zip reader for .xlsx files, using the browser's built-in deflate.
  async function unzip(buffer) {
    const bytes = new Uint8Array(buffer);
    const view = new DataView(buffer);
    let end = -1;
    for (let i = bytes.length - 22; i >= Math.max(0, bytes.length - 65557); i--) {
      if (view.getUint32(i, true) === 0x06054b50) {
        end = i;
        break;
      }
    }
    if (end < 0) throw new Error('This file is not a valid .xlsx file.');
    const count = view.getUint16(end + 10, true);
    let pointer = view.getUint32(end + 16, true);
    const entries = {};
    const decoder = new TextDecoder();
    for (let n = 0; n < count && view.getUint32(pointer, true) === 0x02014b50; n++) {
      const nameLength = view.getUint16(pointer + 28, true);
      const name = decoder.decode(bytes.subarray(pointer + 46, pointer + 46 + nameLength));
      entries[name] = {
        method: view.getUint16(pointer + 10, true),
        size: view.getUint32(pointer + 20, true),
        offset: view.getUint32(pointer + 42, true),
      };
      pointer += 46 + nameLength + view.getUint16(pointer + 30, true) + view.getUint16(pointer + 32, true);
    }
    return async (name) => {
      const entry = entries[name];
      if (!entry) return null;
      const start = entry.offset + 30 + view.getUint16(entry.offset + 26, true) + view.getUint16(entry.offset + 28, true);
      const data = bytes.subarray(start, start + entry.size);
      if (entry.method === 0) return decoder.decode(data);
      if (typeof DecompressionStream === 'undefined') {
        throw new Error('Your browser can’t open .xlsx files. Save the report as .csv and try again.');
      }
      const stream = new Blob([data]).stream().pipeThrough(new DecompressionStream('deflate-raw'));
      return new Response(stream).text();
    };
  }

  const columnIndex = (letters) => {
    let index = 0;
    for (const char of letters) index = index * 26 + (char.charCodeAt(0) - 64);
    return index - 1;
  };

  async function readXlsx(buffer) {
    const read = await unzip(buffer);
    const shared = [];
    const sharedXml = await read('xl/sharedStrings.xml');
    if (sharedXml) {
      const items = sharedXml.match(/<si>[\s\S]*?<\/si>/g) || [];
      for (const item of items) {
        const clean = item.replace(/<rPh\b[\s\S]*?<\/rPh>/g, '');
        const parts = clean.match(/<t\b[^>]*>[\s\S]*?<\/t>/g) || [];
        shared.push(decodeXml(parts.map((part) => part.replace(/<[^>]+>/g, '')).join('')));
      }
    }

    let sheetPath = 'xl/worksheets/sheet1.xml';
    const workbook = await read('xl/workbook.xml');
    const rels = await read('xl/_rels/workbook.xml.rels');
    if (workbook && rels) {
      const firstSheet = workbook.match(/<sheet\b[^>]*r:id="([^"]+)"/);
      if (firstSheet) {
        const rel = rels.match(new RegExp(`<Relationship\\b[^>]*Id="${firstSheet[1]}"[^>]*>`));
        const target = rel && rel[0].match(/Target="([^"]+)"/);
        if (target) sheetPath = target[1].startsWith('/') ? target[1].slice(1) : `xl/${target[1].replace(/^\.\//, '')}`;
      }
    }
    const sheet = await read(sheetPath);
    if (!sheet) throw new Error('Couldn’t find a worksheet in this file.');

    const rows = [];
    const rowPattern = /<row\b([^>]*?)(?:\/>|>([\s\S]*?)<\/row>)/g;
    const cellPattern = /<c\b([^>]*?)(?:\/>|>([\s\S]*?)<\/c>)/g;
    let rowMatch;
    while ((rowMatch = rowPattern.exec(sheet))) {
      const rowNumber = rowMatch[1].match(/\br="(\d+)"/);
      const rowIndex = rowNumber ? Number(rowNumber[1]) - 1 : rows.length;
      const row = [];
      let column = -1;
      let cellMatch;
      cellPattern.lastIndex = 0;
      const body = rowMatch[2] || '';
      while ((cellMatch = cellPattern.exec(body))) {
        const attrs = cellMatch[1];
        const ref = attrs.match(/\br="([A-Z]+)\d+"/);
        column = ref ? columnIndex(ref[1]) : column + 1;
        const type = (attrs.match(/\bt="(\w+)"/) || [])[1];
        const inner = cellMatch[2] || '';
        let value = '';
        if (type === 'inlineStr') {
          value = decodeXml((inner.match(/<t\b[^>]*>([\s\S]*?)<\/t>/g) || []).map((t) => t.replace(/<[^>]+>/g, '')).join(''));
        } else {
          const raw = (inner.match(/<v>([\s\S]*?)<\/v>/) || [])[1];
          if (raw !== undefined) {
            if (type === 's') value = shared[Number(raw)] ?? '';
            else if (type === 'str' || type === 'e') value = decodeXml(raw);
            else if (type === 'b') value = raw === '1';
            else value = Number(raw);
          }
        }
        row[column] = value;
      }
      rows[rowIndex] = row;
    }
    return rows.filter(Boolean);
  }

  function readCsv(text) {
    text = text.replace(/^﻿/, '');
    const firstLine = text.slice(0, text.indexOf('\n') > -1 ? text.indexOf('\n') : text.length);
    const delimiter = [',', '\t', ';'].reduce((best, d) =>
      firstLine.split(d).length > firstLine.split(best).length ? d : best
    );
    const rows = [];
    let row = [];
    let field = '';
    let quoted = false;
    for (let i = 0; i < text.length; i++) {
      const char = text[i];
      if (quoted) {
        if (char === '"') {
          if (text[i + 1] === '"') {
            field += '"';
            i++;
          } else quoted = false;
        } else field += char;
      } else if (char === '"') quoted = true;
      else if (char === delimiter) {
        row.push(field);
        field = '';
      } else if (char === '\n' || char === '\r') {
        if (char === '\r' && text[i + 1] === '\n') i++;
        row.push(field);
        rows.push(row);
        row = [];
        field = '';
      } else field += char;
    }
    if (field !== '' || row.length) {
      row.push(field);
      rows.push(row);
    }
    return rows;
  }

  // ---------- Column mapping ----------

  const normHeader = (header) =>
    String(header ?? '')
      .toLowerCase()
      .replace(/\(#\)/g, '')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();

  const FIELDS = {
    term: { names: ['customer search term', 'search term', 'customer search terms'] },
    campaign: { names: ['campaign name', 'campaign'] },
    adGroup: { names: ['ad group name', 'ad group'] },
    targeting: { names: ['targeting', 'keyword', 'keyword text', 'target', 'targeting expression'] },
    matchType: { names: ['match type'] },
    impressions: { names: ['impressions'] },
    clicks: { names: ['clicks'] },
    spend: { names: ['spend', 'cost', 'total cost'] },
    sales: {
      names: ['7 day total sales', '14 day total sales', 'total sales', 'sales', '7 day sales', 'sales 7d', '14 day sales'],
      test: (h) => /\btotal sales\b/.test(h) || /^sales \d+d$/.test(h),
    },
    orders: {
      names: ['7 day total orders', '14 day total orders', 'total orders', 'orders', 'purchases', 'purchases 7d', '7 day total purchases'],
      test: (h) => /\btotal orders\b/.test(h) || /^purchases\b/.test(h),
    },
    startDate: { names: ['start date', 'date'] },
    endDate: { names: ['end date'] },
    currency: { names: ['currency'] },
  };
  const REQUIRED = { term: 'Customer Search Term', clicks: 'Clicks', spend: 'Spend', sales: 'Sales', orders: 'Orders' };

  function mapColumns(headerRow) {
    const headers = headerRow.map(normHeader);
    const map = {};
    for (const [field, spec] of Object.entries(FIELDS)) {
      let index = -1;
      for (const name of spec.names) {
        index = headers.indexOf(name);
        if (index > -1) break;
      }
      if (index < 0 && spec.test) index = headers.findIndex(spec.test);
      if (index > -1) map[field] = index;
    }
    return map;
  }

  const num = (value) => {
    if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
    const parsed = parseFloat(String(value ?? '').replace(/[^0-9.\-]/g, ''));
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const toDate = (value) => {
    if (value === '' || value === undefined || value === null) return null;
    if (typeof value === 'number' && value > 20000 && value < 80000) {
      return new Date(Date.UTC(1899, 11, 30) + Math.round(value) * 86400000);
    }
    const parsed = new Date(String(value));
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  };

  // Turns raw sheet rows into report records. Throws a friendly error when the file isn't a search term report.
  function toRecords(rows) {
    let headerIndex = -1;
    let map = null;
    for (let i = 0; i < Math.min(rows.length, 15); i++) {
      const candidate = mapColumns(rows[i] || []);
      if (candidate.term !== undefined && candidate.clicks !== undefined) {
        headerIndex = i;
        map = candidate;
        break;
      }
    }
    if (!map) {
      throw new Error(
        'This doesn’t look like a Sponsored Products Search Term report. Look for a "Customer Search Term" column.'
      );
    }
    const missing = Object.keys(REQUIRED).filter((field) => map[field] === undefined);
    if (missing.length) {
      throw new Error(`The report is missing these columns: ${missing.map((field) => REQUIRED[field]).join(', ')}.`);
    }
    const get = (row, field) => (map[field] === undefined ? '' : row[map[field]]);
    const records = [];
    for (const row of rows.slice(headerIndex + 1)) {
      if (!row) continue;
      const term = String(get(row, 'term') ?? '').trim();
      if (!term) continue;
      records.push({
        term,
        campaign: String(get(row, 'campaign') || '—').trim(),
        adGroup: String(get(row, 'adGroup') || '—').trim(),
        targeting: String(get(row, 'targeting') || '').trim(),
        matchType: String(get(row, 'matchType') || '').trim().toUpperCase(),
        impressions: num(get(row, 'impressions')),
        clicks: num(get(row, 'clicks')),
        spend: num(get(row, 'spend')),
        sales: num(get(row, 'sales')),
        orders: num(get(row, 'orders')),
        start: toDate(get(row, 'startDate')),
        end: toDate(get(row, 'endDate')) || toDate(get(row, 'startDate')),
        currency: String(get(row, 'currency') || '').trim(),
      });
    }
    if (!records.length) throw new Error('The report has no search term rows.');
    return records;
  }

  // ---------- Rules ----------

  const termKey = (text) => String(text).toLowerCase().trim().replace(/\s+/g, ' ');
  const asinOf = (targeting) => {
    const match = String(targeting).match(/^asin="?(b0[a-z0-9]{8})"?$/i);
    return match ? match[1].toLowerCase() : null;
  };
  const containsPhrase = (term, phrase) => ` ${term} `.includes(` ${phrase} `);
  const acosOf = (spend, sales) => (sales > 0 ? spend / sales : spend > 0 ? Infinity : 0);
  // Half-cent rounds up; the small nudge keeps float error (0.995 stored as 0.99499…) from rounding down.
  const round2 = (value) => Math.floor(value * 100 + 0.5 + 1e-6) / 100;

  function suggestBid(cpc, acos, settings) {
    if (!(cpc > 0)) return null;
    const ratio = acos > 0 ? settings.target / acos : 1 + settings.maxChange;
    const clamped = Math.min(1 + settings.maxChange, Math.max(1 - settings.maxChange, ratio));
    return Math.max(0.02, round2(cpc * clamped));
  }

  // Combines rows into one entry per search term, keeping each campaign/ad group/target it came through.
  function aggregate(records) {
    const terms = new Map();
    let start = null;
    let end = null;
    let currency = '';
    for (const record of records) {
      if (record.start && (!start || record.start < start)) start = record.start;
      if (record.end && (!end || record.end > end)) end = record.end;
      if (!currency && record.currency) currency = record.currency;
      const key = termKey(record.term);
      let term = terms.get(key);
      if (!term) {
        term = { key, term: record.term.trim(), isAsin: ASIN.test(key), impressions: 0, clicks: 0, spend: 0, sales: 0, orders: 0, placements: new Map() };
        terms.set(key, term);
      }
      term.impressions += record.impressions;
      term.clicks += record.clicks;
      term.spend += record.spend;
      term.sales += record.sales;
      term.orders += record.orders;
      const placeKey = [record.campaign, record.adGroup, record.targeting, record.matchType].join('\u0001');
      let place = term.placements.get(placeKey);
      if (!place) {
        place = { campaign: record.campaign, adGroup: record.adGroup, targeting: record.targeting, matchType: record.matchType, clicks: 0, spend: 0, sales: 0, orders: 0 };
        term.placements.set(placeKey, place);
      }
      place.clicks += record.clicks;
      place.spend += record.spend;
      place.sales += record.sales;
      place.orders += record.orders;
    }
    const list = [];
    for (const term of terms.values()) {
      term.placements = [...term.placements.values()].sort((a, b) => b.spend - a.spend);
      term.acos = acosOf(term.spend, term.sales);
      term.cpc = term.clicks > 0 ? term.spend / term.clicks : 0;
      term.cvr = term.clicks > 0 ? term.orders / term.clicks : 0;
      // A term is "already harvested" when it already runs as its own exact keyword or ASIN target.
      term.harvested = term.placements.some((p) =>
        term.isAsin ? asinOf(p.targeting) === term.key : p.matchType === 'EXACT' && termKey(p.targeting) === term.key
      );
      term.phraseRunning = term.placements.some((p) => p.matchType === 'PHRASE' && termKey(p.targeting) === term.key);
      list.push(term);
    }
    return { terms: list, start, end, currency };
  }

  function classify(term, s) {
    if (term.orders > 0) {
      if (term.acos <= s.target) return term.orders >= s.winnerOrders ? 'winner' : 'promising';
      return term.acos <= s.target * s.wayMult ? 'slightly' : 'way';
    }
    if (term.clicks >= s.loserClicks) return 'loser';
    if (term.clicks >= s.cautionClicks) return 'caution';
    return 'noise';
  }

  // Finds single words and 2-word phrases that never converted across 2+ search terms.
  function findPhraseNegatives(terms, s) {
    const grams = new Map();
    for (const term of terms) {
      if (term.isAsin) continue;
      const words = term.key.split(' ');
      const seen = new Set();
      words.forEach((word, i) => {
        if (word.length > 1 && !STOPWORDS.has(word)) seen.add(word);
        const next = words[i + 1];
        if (next && !(STOPWORDS.has(word) && STOPWORDS.has(next))) seen.add(`${word} ${next}`);
      });
      for (const gram of seen) {
        let stat = grams.get(gram);
        if (!stat) {
          stat = { phrase: gram, words: gram.split(' ').length, terms: [], clicks: 0, spend: 0, orders: 0 };
          grams.set(gram, stat);
        }
        stat.terms.push(term);
        stat.clicks += term.clicks;
        stat.spend += term.spend;
        stat.orders += term.orders;
      }
    }
    const picked = [];
    const pickedWords = new Set();
    const candidates = [...grams.values()]
      .filter((g) => g.orders === 0 && g.clicks >= s.loserClicks && g.terms.length >= 2)
      .sort((a, b) => a.words - b.words || b.spend - a.spend);
    for (const gram of candidates) {
      if (gram.words === 2 && gram.phrase.split(' ').some((word) => pickedWords.has(word))) continue;
      picked.push(gram);
      if (gram.words === 1) pickedWords.add(gram.phrase);
    }
    return picked.sort((a, b) => b.spend - a.spend);
  }

  const dateStamp = () => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  };

  function audit(base, settings) {
    const s = Object.assign({}, DEFAULTS, settings);
    const stamp = dateStamp();
    const names = {
      exact: `BDT - Exact - Winners - ${stamp}`,
      phrase: `BDT - Phrase - Testing - ${stamp}`,
      product: `BDT - Product Targeting - Winners - ${stamp}`,
    };
    const buckets = Object.fromEntries(BUCKETS.map((b) => [b.key, []]));
    const totals = { terms: 0, clicks: 0, spend: 0, sales: 0, orders: 0 };
    for (const term of base.terms) {
      term.bucket = classify(term, s);
      term.coveredBy = null;
      term.bid = ['winner', 'slightly', 'way'].includes(term.bucket) ? suggestBid(term.cpc, term.acos, s) : null;
      buckets[term.bucket].push(term);
      totals.terms += 1;
      totals.clicks += term.clicks;
      totals.spend += term.spend;
      totals.sales += term.sales;
      totals.orders += term.orders;
    }
    for (const list of Object.values(buckets)) list.sort((a, b) => b.spend - a.spend);

    const phrases = findPhraseNegatives(base.terms, s);
    const phraseCaught = new Set();
    for (const gram of phrases) for (const term of gram.terms) phraseCaught.add(term);
    for (const term of buckets.loser) {
      if (term.isAsin) continue;
      const gram = phrases.find((g) => containsPhrase(term.key, g.phrase));
      if (gram) term.coveredBy = gram.phrase;
    }

    const sum = (list) => list.reduce((total, term) => total + term.spend, 0);
    const noOrder = [...buckets.loser, ...buckets.caution, ...buckets.noise];
    const waste = {
      confirmed: sum(buckets.loser),
      atRisk: sum(buckets.caution),
      noOrder: sum(noOrder),
      phrase: sum([...phraseCaught]),
    };

    // Negatives, one row per campaign/ad group where the term spent.
    const negExact = [];
    const negProduct = [];
    const seen = new Set();
    const addNegative = (list, term, place, reason) => {
      const isSelf = term.isAsin
        ? asinOf(place.targeting) === term.key
        : place.matchType === 'EXACT' && termKey(place.targeting) === term.key;
      const id = [list === negExact ? 'e' : 'p', place.campaign, place.adGroup, term.key].join('\u0001');
      if (seen.has(id)) return;
      seen.add(id);
      list.push({
        campaign: place.campaign,
        adGroup: place.adGroup,
        value: term.isAsin ? term.key.toUpperCase() : term.key,
        action: isSelf ? (term.isAsin ? 'Pause this ASIN target' : 'Pause this exact keyword') : term.isAsin ? 'Add negative product target' : 'Add negative exact',
        reason,
        clicks: term.clicks,
        spend: term.spend,
      });
    };
    for (const term of buckets.loser) {
      if (term.coveredBy) continue;
      const reason = `Loser: 0 orders from ${term.clicks} clicks`;
      for (const place of term.placements) addNegative(term.isAsin ? negProduct : negExact, term, place, reason);
    }
    for (const term of buckets.winner) {
      if (term.harvested) continue;
      const reason = term.isAsin ? `Moved to ${names.product}` : `Moved to ${names.exact}`;
      for (const place of term.placements) addNegative(term.isAsin ? negProduct : negExact, term, place, reason);
    }

    const negPhrase = [];
    const phraseNotes = [];
    for (const gram of phrases) {
      const places = new Map();
      for (const term of gram.terms) {
        for (const place of term.placements) {
          const id = `${place.campaign}\u0001${place.adGroup}`;
          const ownKeyword = !asinOf(place.targeting) && place.matchType && place.matchType !== '-' && containsPhrase(termKey(place.targeting), gram.phrase);
          if (ownKeyword) {
            phraseNotes.push({ phrase: gram.phrase, campaign: place.campaign, adGroup: place.adGroup, keyword: place.targeting });
            continue;
          }
          if (!places.has(id)) places.set(id, place);
        }
      }
      for (const place of places.values()) {
        negPhrase.push({
          campaign: place.campaign,
          adGroup: place.adGroup,
          value: gram.phrase,
          action: 'Add negative phrase',
          reason: `0 orders from ${gram.clicks} clicks across ${gram.terms.length} search terms`,
          clicks: gram.clicks,
          spend: gram.spend,
        });
      }
    }

    // New campaigns.
    const sources = (term) => [...new Set(term.placements.map((p) => p.campaign))].join(' | ');
    const exactWinners = buckets.winner
      .filter((t) => !t.isAsin && !t.harvested)
      .map((t) => ({ campaign: names.exact, adGroup: 'Winners', value: t.key, match: 'Exact', bid: t.bid, term: t, sources: sources(t) }));
    const phraseTesting = buckets.promising
      .filter((t) => !t.isAsin && !t.harvested && !t.phraseRunning)
      .map((t) => ({ campaign: names.phrase, adGroup: 'Testing', value: t.key, match: 'Phrase', bid: t.cpc > 0 ? Math.max(0.02, round2(t.cpc)) : null, term: t, sources: sources(t) }));
    const productTargets = [...buckets.winner, ...buckets.promising]
      .filter((t) => t.isAsin && !t.harvested)
      .sort((a, b) => b.orders - a.orders || b.spend - a.spend)
      .map((t) => ({
        campaign: names.product,
        adGroup: 'Competitor ASINs',
        value: t.key.toUpperCase(),
        match: 'ASIN target',
        bid: t.bucket === 'winner' ? t.bid : t.cpc > 0 ? Math.max(0.02, round2(t.cpc)) : null,
        term: t,
        sources: sources(t),
      }));

    // Bid changes: over-target terms everywhere they spent, plus harvested winners on their own exact keyword.
    const bidChanges = [];
    for (const term of [...buckets.winner, ...buckets.slightly, ...buckets.way]) {
      if (term.bid === null) continue;
      if (term.bucket === 'winner' && !term.harvested) continue;
      for (const place of term.placements) {
        const isSelf = term.isAsin ? asinOf(place.targeting) === term.key : place.matchType === 'EXACT' && termKey(place.targeting) === term.key;
        if (term.bucket === 'winner' && !isSelf) continue;
        const through = place.targeting || 'its target';
        bidChanges.push({
          term,
          place,
          how: isSelf
            ? `Set this ${term.isAsin ? 'ASIN target' : 'exact keyword'} bid to ${term.bid.toFixed(2)}`
            : `Came through "${through}". Lower that bid, or add the term as its own exact keyword at ${term.bid.toFixed(2)}`,
        });
      }
    }

    return {
      settings: s,
      names,
      totals,
      acos: acosOf(totals.spend, totals.sales),
      buckets,
      waste,
      phrases,
      negExact,
      negPhrase,
      negProduct,
      phraseNotes,
      exactWinners,
      phraseTesting,
      productTargets,
      bidChanges,
      start: base.start,
      end: base.end,
      currency: base.currency,
    };
  }

  // ---------- CSV output ----------

  const csvCell = (value) => {
    const text = value === null || value === undefined ? '' : String(value);
    return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
  };
  const toCsv = (rows) => '﻿' + rows.map((row) => row.map(csvCell).join(',')).join('\r\n');
  const pct = (ratio) => (Number.isFinite(ratio) ? `${(ratio * 100).toFixed(1)}%` : ratio === Infinity ? 'No sales' : '');
  const fixed = (value) => (value === null || value === undefined ? '' : value.toFixed(2));

  function buildFiles(result) {
    const label = Object.fromEntries(BUCKETS.map((b) => [b.key, b.label]));
    const negRows = (list) => [
      ['Campaign', 'Ad group', 'Negative', 'Action', 'Reason', 'Clicks', 'Spend'],
      ...list.map((r) => [r.campaign, r.adGroup, r.value, r.action, r.reason, r.clicks, fixed(r.spend)]),
    ];
    const newRows = (list) => [
      ['Campaign', 'Ad group', 'Keyword or target', 'Match type', 'Starting bid', 'Orders', 'ACoS', 'Clicks', 'Spend', 'Found in campaigns'],
      ...list.map((r) => [r.campaign, r.adGroup, r.value, r.match, fixed(r.bid), r.term.orders, pct(r.term.acos), r.term.clicks, fixed(r.term.spend), r.sources]),
    ];
    const files = {
      'negative-exact.csv': negRows(result.negExact),
      'negative-phrase.csv': negRows(result.negPhrase),
      'negative-product-targets.csv': negRows(result.negProduct),
      'new-exact-keywords.csv': newRows(result.exactWinners),
      'new-phrase-keywords.csv': newRows(result.phraseTesting),
      'new-product-targets.csv': newRows(result.productTargets),
      'bid-changes.csv': [
        ['Campaign', 'Ad group', 'Targeting', 'Match type', 'Search term', 'Bucket', 'Clicks', 'Spend', 'Sales', 'Orders', 'ACoS', 'Avg CPC', 'Suggested bid', 'How to apply'],
        ...result.bidChanges.map(({ term, place, how }) => [
          place.campaign, place.adGroup, place.targeting, place.matchType, term.key, label[term.bucket],
          term.clicks, fixed(term.spend), fixed(term.sales), term.orders, pct(term.acos), fixed(term.cpc), fixed(term.bid), how,
        ]),
      ],
    };
    const all = BUCKETS.flatMap((b) => result.buckets[b.key]);
    files['full-audit.csv'] = [
      ['Search term', 'Type', 'Bucket', 'Impressions', 'Clicks', 'Spend', 'Sales', 'Orders', 'ACoS', 'Avg CPC', 'Conversion rate', 'Suggested bid', 'Note', 'Campaigns'],
      ...all.map((t) => [
        t.isAsin ? t.key.toUpperCase() : t.key, t.isAsin ? 'ASIN' : 'Keyword', label[t.bucket], t.impressions, t.clicks, fixed(t.spend), fixed(t.sales), t.orders,
        pct(t.acos), fixed(t.cpc), pct(t.cvr), fixed(t.bid),
        t.harvested ? 'Already harvested' : t.coveredBy ? `Covered by negative phrase "${t.coveredBy}"` : '',
        [...new Set(t.placements.map((p) => p.campaign))].join(' | '),
      ]),
    ];
    return Object.fromEntries(Object.entries(files).map(([name, rows]) => [name, toCsv(rows)]));
  }

  // ---------- Sample data (made up, for people without a report handy) ----------

  function sampleRecords() {
    const auto = 'Cutting Board - Auto';
    const broad = 'Cutting Board - Broad';
    const exact = 'Cutting Board - Exact';
    const rows = [
      // term, campaign, targeting, match, clicks, spend, orders, sales
      ['bamboo cutting board', exact, 'bamboo cutting board', 'EXACT', 120, 102, 14, 419.3],
      ['bamboo cutting board', auto, 'close-match', '-', 18, 17.1, 2, 59.8],
      ['bamboo cutting board set', broad, 'bamboo cutting board', 'BROAD', 64, 55.04, 9, 269.1],
      ['cutting board with juice groove', auto, 'close-match', '-', 41, 36.9, 5, 149.5],
      ['large wooden cutting board', broad, 'wooden cutting board', 'BROAD', 38, 33.44, 4, 119.6],
      ['b07k9qz1lm', auto, 'substitutes', '-', 22, 17.6, 3, 89.7],
      ['bamboo chopping board', auto, 'loose-match', '-', 14, 11.2, 2, 59.8],
      ['cutting boards for kitchen', broad, 'cutting board', 'BROAD', 9, 7.2, 1, 29.9],
      ['eco friendly cutting board', auto, 'close-match', '-', 7, 5.6, 1, 29.9],
      ['b08hx2wq4c', auto, 'substitutes', '-', 8, 6.4, 1, 29.9],
      ['cutting board', broad, 'cutting board', 'BROAD', 150, 171, 15, 448.5],
      ['cutting board', auto, 'loose-match', '-', 60, 68.4, 6, 179.4],
      ['wood cutting board', broad, 'wooden cutting board', 'BROAD', 55, 60.5, 5, 149.5],
      ['kitchen cutting board', broad, 'cutting board', 'BROAD', 48, 57.6, 2, 59.8],
      ['butcher block cutting board', auto, 'loose-match', '-', 33, 41.25, 1, 39.9],
      ['plastic cutting board', broad, 'cutting board', 'BROAD', 44, 41.8, 0, 0],
      ['cutting board mat flexible', auto, 'loose-match', '-', 31, 26.35, 0, 0],
      ['b09rtm7k2p', auto, 'complements', '-', 34, 23.8, 0, 0],
      ['glass cutting board', broad, 'cutting board', 'BROAD', 16, 13.6, 0, 0],
      ['cutting board oil', auto, 'complements', '-', 12, 7.8, 0, 0],
      ['marble cutting board', broad, 'cutting board', 'BROAD', 11, 10.45, 0, 0],
      ['plastic cutting board dishwasher safe', auto, 'loose-match', '-', 6, 5.1, 0, 0],
      ['plastic chopping board', auto, 'loose-match', '-', 4, 3.2, 0, 0],
      ['flexible cutting mats', auto, 'loose-match', '-', 5, 4.25, 0, 0],
      ['glass cutting board set', broad, 'cutting board', 'BROAD', 3, 2.7, 0, 0],
      ['cutting board stand', auto, 'complements', '-', 4, 2.4, 0, 0],
      ['knife set', auto, 'complements', '-', 3, 2.85, 0, 0],
      ['cheese board', auto, 'loose-match', '-', 5, 4.5, 0, 0],
      ['charcuterie board', broad, 'cutting board', 'BROAD', 6, 6.3, 0, 0],
      ['acacia cutting board', broad, 'wooden cutting board', 'BROAD', 2, 1.9, 0, 0],
      ['b0c4lnv8tz', auto, 'substitutes', '-', 3, 2.1, 0, 0],
      ['bamboo serving tray', auto, 'loose-match', '-', 2, 1.6, 0, 0],
      ['epicurean cutting board', broad, 'cutting board', 'BROAD', 2, 2.2, 0, 0],
      ['cutting board with handle', auto, 'close-match', '-', 1, 0.9, 0, 0],
    ];
    const end = new Date();
    const start = new Date(end.getTime() - 29 * 86400000);
    return rows.map(([term, campaign, targeting, matchType, clicks, spend, orders, sales]) => ({
      term, campaign, adGroup: campaign, targeting, matchType, impressions: clicks * 38, clicks, spend, sales, orders, start, end, currency: 'USD',
    }));
  }

  window.BDTPPCAudit = { BUCKETS, DEFAULTS, readXlsx, readCsv, toRecords, aggregate, audit, buildFiles, sampleRecords };

  // ---------- Page element ----------

  if (typeof customElements === 'undefined' || customElements.get('ppc-audit')) return;

  const el = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  };

  const PAGE = 100;

  class PPCAudit extends HTMLElement {
    connectedCallback() {
      this.tools = window.BDTTools;
      this.base = null;
      this.result = null;
      this.files = null;
      this.tab = 'winner';
      this.sort = { key: 'spend', dir: -1 };
      this.limit = PAGE;
      this.open = new Set();

      this.drop = this.querySelector('[data-drop]');
      this.input = this.querySelector('[data-file]');
      this.status = this.querySelector('[data-status]');
      this.settingsForm = this.querySelector('[data-settings]');
      this.filtersForm = this.querySelector('[data-filters]');
      this.results = this.querySelector('[data-results]');

      this.input.addEventListener('change', () => this.input.files[0] && this.load(this.input.files[0]));
      ['dragenter', 'dragover'].forEach((type) =>
        this.drop.addEventListener(type, (event) => {
          event.preventDefault();
          this.drop.classList.add('is-over');
        })
      );
      ['dragleave', 'drop'].forEach((type) =>
        this.drop.addEventListener(type, () => this.drop.classList.remove('is-over'))
      );
      this.drop.addEventListener('drop', (event) => {
        event.preventDefault();
        const file = event.dataTransfer && event.dataTransfer.files[0];
        if (file) this.load(file);
      });
      const sample = this.querySelector('[data-sample]');
      if (sample) {
        sample.addEventListener('click', () => {
          this.fileName = 'Sample report (made-up numbers)';
          this.base = window.BDTPPCAudit.aggregate(window.BDTPPCAudit.sampleRecords());
          this.setStatus('Showing a made-up sample report. Upload your own to audit your account.');
          this.run(true);
        });
      }

      this.settingsForm.addEventListener('submit', (event) => event.preventDefault());
      this.settingsForm.addEventListener('input', () => {
        clearTimeout(this._timer);
        this._timer = setTimeout(() => this.base && this.run(false), 250);
      });
      this.settingsForm.addEventListener('reset', () => setTimeout(() => this.base && this.run(false)));
      this.filtersForm.addEventListener('submit', (event) => event.preventDefault());
      this.filtersForm.addEventListener('input', () => {
        this.limit = PAGE;
        this.renderTable();
      });
      this.filtersForm.addEventListener('reset', () =>
        setTimeout(() => {
          this.limit = PAGE;
          this.renderTable();
        })
      );

      this.querySelectorAll('[data-tab]').forEach((button) =>
        button.addEventListener('click', () => this.selectTab(button.dataset.tab))
      );
      this.querySelector('[data-table-head]').addEventListener('click', (event) => {
        const th = event.target.closest('[data-sort]');
        if (!th) return;
        const key = th.dataset.sort;
        this.sort = { key, dir: this.sort.key === key ? -this.sort.dir : key === 'term' ? 1 : -1 };
        this.renderTable();
      });
      this.querySelector('[data-table-body]').addEventListener('click', (event) => {
        const row = event.target.closest('[data-term]');
        if (!row) return;
        const key = row.dataset.term;
        if (this.open.has(key)) this.open.delete(key);
        else this.open.add(key);
        this.renderTable();
      });
      this.querySelector('[data-more]').addEventListener('click', () => {
        this.limit += PAGE;
        this.renderTable();
      });
      this.addEventListener('click', (event) => {
        const download = event.target.closest('[data-download]');
        if (download) this.download(download.dataset.download);
        const copy = event.target.closest('[data-copy]');
        if (copy) this.copyList(copy.dataset.copy, copy);
        const promptCopy = event.target.closest('[data-copy-prompt]');
        if (promptCopy) this.tools.copy(this.querySelector('[data-out="prompt"]').value, promptCopy);
      });
    }

    setStatus(message, isError) {
      this.status.textContent = message;
      this.status.classList.toggle('is-error', Boolean(isError));
      this.status.hidden = !message;
    }

    async load(file) {
      this.setStatus(`Reading ${file.name}…`);
      try {
        const engine = window.BDTPPCAudit;
        let rows;
        if (/\.xlsx$/i.test(file.name)) rows = await engine.readXlsx(await file.arrayBuffer());
        else if (/\.(csv|tsv|txt)$/i.test(file.name)) rows = engine.readCsv(await file.text());
        else throw new Error('Upload the report as an .xlsx or .csv file.');
        this.base = engine.aggregate(engine.toRecords(rows));
        this.fileName = file.name;
        this.setStatus('');
        this.run(true);
      } catch (error) {
        this.setStatus(error.message || 'Couldn’t read that file.', true);
      } finally {
        this.input.value = '';
      }
    }

    readSettings() {
      const value = (name) => {
        const input = this.settingsForm.querySelector(`[name="${name}"]`);
        const parsed = parseFloat(String(input.value).replace(/[^0-9.]/g, ''));
        return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
      };
      const d = DEFAULTS;
      return {
        target: (value('target') ?? d.target * 100) / 100,
        wayMult: Math.max(1, value('wayMult') ?? d.wayMult),
        winnerOrders: Math.max(1, Math.round(value('winnerOrders') ?? d.winnerOrders)),
        loserClicks: Math.max(1, Math.round(value('loserClicks') ?? d.loserClicks)),
        cautionClicks: Math.max(1, Math.round(value('cautionClicks') ?? d.cautionClicks)),
        maxChange: Math.min(0.95, (value('maxChange') ?? d.maxChange * 100) / 100),
      };
    }

    run(fresh) {
      const engine = window.BDTPPCAudit;
      this.result = engine.audit(this.base, this.readSettings());
      this.files = engine.buildFiles(this.result);
      this.currency = this.result.currency || this.dataset.currency || 'USD';
      if (fresh) {
        this.open.clear();
        this.limit = PAGE;
        const first = BUCKETS.find((b) => this.result.buckets[b.key].length && b.key !== 'noise');
        this.tab = first ? first.key : 'winner';
      }
      this.results.hidden = false;
      this.renderSummary();
      this.renderPlan();
      this.selectTab(this.tab);
      if (fresh) {
        this.results.scrollIntoView({ behavior: 'smooth', block: 'start' });
        this.tools.track('ppc-audit', { terms: this.result.totals.terms });
      }
    }

    money(value, cents) {
      try {
        return new Intl.NumberFormat(undefined, {
          style: 'currency',
          currency: this.currency,
          minimumFractionDigits: cents ? 2 : 0,
          maximumFractionDigits: cents ? 2 : 0,
        }).format(value);
      } catch (e) {
        return '$' + value.toFixed(cents ? 2 : 0);
      }
    }

    acos(value) {
      if (value === Infinity) return 'No sales';
      return this.tools.percent(value);
    }

    out(name, text) {
      this.querySelectorAll(`[data-out="${name}"]`).forEach((node) => (node.textContent = text));
    }

    renderSummary() {
      const r = this.result;
      const date = (d) => d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
      const range = r.start && r.end ? `${date(r.start)} to ${date(r.end)}` : '';
      this.out('file', [this.fileName, range].filter(Boolean).join(' · '));
      this.out('spend', this.money(r.totals.spend));
      this.out('sales', this.money(r.totals.sales));
      this.out('acos', this.acos(r.acos));
      this.out('terms', r.totals.terms.toLocaleString());
      this.out('confirmed', this.money(r.waste.confirmed));
      this.out('confirmed-sub', `${r.buckets.loser.length.toLocaleString()} losers · ${this.tools.percent(r.totals.spend ? r.waste.confirmed / r.totals.spend : 0)} of spend`);
      this.out('at-risk', this.money(r.waste.atRisk));
      this.out('at-risk-sub', `${r.buckets.caution.length.toLocaleString()} caution terms`);
      this.out('no-order', this.money(r.waste.noOrder));
      this.out('no-order-sub', `${this.tools.percent(r.totals.spend ? r.waste.noOrder / r.totals.spend : 0)} of spend, noise included`);
      this.out('phrase-waste', this.money(r.waste.phrase));
      this.out('phrase-waste-sub', `${r.phrases.length} losing words and phrases`);

      const bar = this.querySelector('[data-out="mix"]');
      const legend = this.querySelector('[data-out="legend"]');
      bar.replaceChildren();
      legend.replaceChildren();
      for (const bucket of BUCKETS) {
        const list = r.buckets[bucket.key];
        const spend = list.reduce((total, term) => total + term.spend, 0);
        const share = r.totals.spend ? spend / r.totals.spend : 0;
        if (share > 0) {
          const segment = el('span', 'bdt-ppc__mix-seg');
          segment.style.width = `${share * 100}%`;
          segment.style.background = bucket.color;
          segment.title = `${bucket.label}: ${this.tools.percent(share)} of spend`;
          bar.appendChild(segment);
        }
        const item = el('li', 'bdt-ppc__legend-item');
        const swatch = el('span', 'bdt-ppc__swatch');
        swatch.style.background = bucket.color;
        item.append(
          swatch,
          el('span', 'bdt-ppc__legend-label', bucket.label),
          el('span', 'bdt-ppc__legend-value', `${list.length.toLocaleString()} terms · ${this.money(spend)} · ${this.tools.percent(share)}`)
        );
        legend.appendChild(item);
      }

      this.querySelectorAll('[data-tab]').forEach((button) => {
        const count = button.querySelector('[data-count]');
        if (count) count.textContent = r.buckets[button.dataset.tab].length.toLocaleString();
      });
    }

    renderPlan() {
      const r = this.result;
      const unique = (list) => new Set(list.map((row) => row.value)).size;
      const exactCount = unique(r.negExact.filter((row) => row.action === 'Add negative exact'));
      this.out('neg-exact-count', exactCount.toLocaleString());
      this.out('neg-phrase-count', r.phrases.length.toLocaleString());
      this.out('neg-product-count', unique(r.negProduct.filter((row) => row.action === 'Add negative product target')).toLocaleString());
      this.out('exact-count', r.exactWinners.length.toLocaleString());
      this.out('phrase-count', r.phraseTesting.length.toLocaleString());
      this.out('product-count', r.productTargets.length.toLocaleString());
      this.out('bid-count', new Set(r.bidChanges.map((row) => row.term.key)).size.toLocaleString());
      this.out('exact-name', r.names.exact);
      this.out('phrase-name', r.names.phrase);
      this.out('product-name', r.names.product);

      const pauses = [...r.negExact, ...r.negProduct].filter((row) => row.action.startsWith('Pause')).length;
      const note = this.querySelector('[data-out="pause-note"]');
      note.hidden = !pauses && !r.phraseNotes.length;
      const parts = [];
      if (pauses) parts.push(`${pauses} of these are your own exact keywords or ASIN targets, so the CSV says to pause them instead of negating.`);
      if (r.phraseNotes.length) parts.push(`${r.phraseNotes.length} ad groups have a keyword that contains a losing phrase, so they're left off the phrase list. Check those keywords.`);
      note.textContent = parts.join(' ');

      const phraseList = this.querySelector('[data-out="phrase-list"]');
      phraseList.replaceChildren();
      for (const gram of r.phrases.slice(0, 12)) {
        const chip = el('li', 'bdt-ppc__chip', gram.phrase);
        chip.title = `${gram.clicks} clicks, ${this.money(gram.spend, true)}, 0 orders across ${gram.terms.length} terms`;
        phraseList.appendChild(chip);
      }
      if (r.phrases.length > 12) phraseList.appendChild(el('li', 'bdt-ppc__chip bdt-ppc__chip--more', `+${r.phrases.length - 12} more`));

      this.querySelectorAll('[data-download], [data-copy]').forEach((button) => {
        const empty = button.dataset.download
          ? this.files[button.dataset.download] && this.files[button.dataset.download].split('\r\n').length <= 1
          : this.listFor(button.dataset.copy).length === 0;
        button.disabled = Boolean(empty);
      });
    }

    listFor(name) {
      const r = this.result;
      const values = (list) => [...new Set(list.map((row) => row.value))];
      switch (name) {
        case 'neg-exact':
          return values(r.negExact.filter((row) => row.action === 'Add negative exact'));
        case 'neg-phrase':
          return r.phrases.map((gram) => gram.phrase);
        case 'neg-product':
          return values(r.negProduct.filter((row) => row.action === 'Add negative product target'));
        case 'exact':
          return r.exactWinners.map((row) => `${row.value}\t${row.bid === null ? '' : row.bid.toFixed(2)}`);
        case 'phrase':
          return r.phraseTesting.map((row) => `${row.value}\t${row.bid === null ? '' : row.bid.toFixed(2)}`);
        case 'product':
          return r.productTargets.map((row) => `${row.value}\t${row.bid === null ? '' : row.bid.toFixed(2)}`);
        default:
          return [];
      }
    }

    copyList(name, button) {
      const list = this.listFor(name);
      if (list.length) this.tools.copy(list.join('\n'), button);
    }

    download(name) {
      if (!this.files || !this.files[name]) return;
      const blob = new Blob([this.files[name]], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `ppc-audit-${name}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    }

    selectTab(key) {
      if (key !== this.tab) {
        this.limit = PAGE;
        this.open.clear();
      }
      this.tab = key;
      this.querySelectorAll('[data-tab]').forEach((button) => {
        const active = button.dataset.tab === key;
        button.setAttribute('aria-selected', String(active));
        button.tabIndex = active ? 0 : -1;
      });
      const bucket = BUCKETS.find((b) => b.key === key);
      this.out('tab-desc', bucket.desc);
      this.renderTable();
    }

    filtered() {
      const read = (name) => {
        const input = this.filtersForm.querySelector(`[name="${name}"]`);
        const parsed = parseFloat(String(input.value).replace(/[^0-9.]/g, ''));
        return Number.isFinite(parsed) ? parsed : null;
      };
      const minClicks = read('minClicks');
      const minOrders = read('minOrders');
      const minSpend = read('minSpend');
      const maxAcos = read('maxAcos');
      const query = termKeyText(this.filtersForm.querySelector('[name="search"]').value);
      return this.result.buckets[this.tab].filter(
        (t) =>
          (minClicks === null || t.clicks >= minClicks) &&
          (minOrders === null || t.orders >= minOrders) &&
          (minSpend === null || t.spend >= minSpend) &&
          (maxAcos === null || t.acos * 100 <= maxAcos) &&
          (!query || t.key.includes(query))
      );
    }

    renderTable() {
      if (!this.result) return;
      const rows = this.filtered();
      const { key, dir } = this.sort;
      rows.sort((a, b) => {
        if (key === 'term') return a.key.localeCompare(b.key) * dir;
        const av = a[key] ?? -1;
        const bv = b[key] ?? -1;
        return (av - bv) * dir || b.spend - a.spend;
      });
      this.querySelectorAll('[data-sort]').forEach((th) => {
        th.setAttribute('aria-sort', th.dataset.sort === key ? (dir > 0 ? 'ascending' : 'descending') : 'none');
      });

      const showBid = ['winner', 'slightly', 'way'].includes(this.tab);
      this.querySelectorAll('[data-col="bid"]').forEach((node) => (node.hidden = !showBid));
      const body = this.querySelector('[data-table-body]');
      body.replaceChildren();
      const columns = showBid ? 9 : 8;
      for (const term of rows.slice(0, this.limit)) {
        const tr = el('tr', 'bdt-ppc__row');
        tr.dataset.term = term.key;
        const open = this.open.has(term.key);
        tr.setAttribute('aria-expanded', String(open));
        const th = el('th', 'bdt-ppc__term');
        th.scope = 'row';
        const toggle = el('button', 'bdt-ppc__toggle');
        toggle.type = 'button';
        toggle.setAttribute('aria-label', `${open ? 'Hide' : 'Show'} campaigns for ${term.key}`);
        toggle.textContent = open ? '−' : '+';
        th.append(toggle, el('span', '', term.isAsin ? term.key.toUpperCase() : term.key));
        if (term.isAsin) th.appendChild(el('span', 'bdt-ppc__tag', 'ASIN'));
        if (term.harvested) th.appendChild(el('span', 'bdt-ppc__tag', 'Already harvested'));
        if (term.coveredBy) th.appendChild(el('span', 'bdt-ppc__tag', `Covered by "${term.coveredBy}"`));
        tr.append(
          th,
          el('td', '', term.impressions.toLocaleString()),
          el('td', '', term.clicks.toLocaleString()),
          el('td', '', this.money(term.spend, true)),
          el('td', '', this.money(term.sales, true)),
          el('td', '', term.orders.toLocaleString()),
          el('td', '', this.acos(term.acos)),
          el('td', '', term.cpc ? this.money(term.cpc, true) : '—')
        );
        if (showBid) tr.appendChild(el('td', 'bdt-ppc__bid', term.bid === null ? '—' : this.money(term.bid, true)));
        body.appendChild(tr);
        if (open) {
          const detail = el('tr', 'bdt-ppc__detail');
          const cell = el('td');
          cell.colSpan = columns;
          const list = el('ul', 'bdt-ppc__places');
          for (const place of term.placements) {
            const item = el('li');
            item.append(
              el('strong', '', place.campaign),
              el('span', '', ` · ${place.adGroup !== place.campaign ? `${place.adGroup} · ` : ''}${place.targeting || '—'}${place.matchType && place.matchType !== '-' ? ` (${place.matchType.toLowerCase()})` : ''}`),
              el('span', 'bdt-ppc__place-stats', `${place.clicks} clicks · ${this.money(place.spend, true)} · ${place.orders} orders · ACoS ${this.acos(acosOf(place.spend, place.sales))}`)
            );
            list.appendChild(item);
          }
          cell.appendChild(list);
          detail.appendChild(cell);
          body.appendChild(detail);
        }
      }
      if (!rows.length) {
        const tr = el('tr');
        const td = el('td', 'bdt-ppc__none', 'No search terms in this bucket match your filters.');
        td.colSpan = columns;
        tr.appendChild(td);
        body.appendChild(tr);
      }
      const total = this.result.buckets[this.tab].length;
      this.out('showing', rows.length === total ? `${total.toLocaleString()} terms` : `${rows.length.toLocaleString()} of ${total.toLocaleString()} terms match your filters`);
      const more = this.querySelector('[data-more]');
      more.hidden = rows.length <= this.limit;
      more.textContent = `Show ${Math.min(PAGE, rows.length - this.limit)} more`;
    }
  }

  const termKeyText = (text) => String(text || '').toLowerCase().trim().replace(/\s+/g, ' ');

  customElements.define('ppc-audit', PPCAudit);
})();
